import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Checkbox, Typography } from "@mui/material";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";
import Title from "../../components/Title";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardIos";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { GET_COMMUNITIES, GET_GROUP } from "../../constants/api";
import Layout from "../../Layout";
import {
  addAllToConnections,
  addToGroups,
  addToPostCommunities,
  clearConnections,
  clearGroups,
  addAllToGroups,
} from "../../redux/postSlice";
import useLoggedIn from "../../hooks/useLoggedIn";
import SelectGroup from "./SelectGroup";
import SelectConn from "./SelectConn";
import Loader from "@components/Loader";
import axios from "@MyAxios";
import SelectPostCom from "./SelectPostCom";
import { useTheme } from "@emotion/react";

const useStyles = makeStyles((theme) => ({
  textColor: { color: theme.palette.primary.main },
  button: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:disabled": { opacity: 0.5 },
  },
  textGrey: { color: "#626262" },
  bgGrey: { backgroundColor: "#efefef" },
  colorGreen: { backgroundColor: "#e5f8f1" },
}));

function AddComCon({
  open = false,
  hideCommunitySelector = false,
  groupId = null,
  isEditMode = false,
  originalGroupIds = [], // ✅ stable list of group IDs from the saved event
}) {
  useLoggedIn();
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const firstRender = useRef(true);

  const [showCommunities, setShowCommunities] = useState(false);
  const [additionalSettingsClicked, setAdditionalSettingsClicked] =
    useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [showConnections, setShowConnections] = useState(false);
  const post = useSelector((state) => state.post);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [communities, setCommunities] = useState([]);
  const {
    connections,
    selectedGroups,
    selectedPostCommunities,
    isGroupOnlyPost,
  } = useSelector((state) => state.post);
  const { userRecord } = useSelector((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [groupIsPrivate, setGroupIsPrivate] = useState(false);
  const [communitySelectionLoading, setCommunitySelectionLoading] =
    useState(false);
  const [singleComGroupChoice, setSingleComGroupChoice] = useState("");
  const [singleCommunityDataFetched, setSingleCommunityDataFetched] =
    useState(false);
  const isAllSelected =
    Boolean(users?.length) && connections?.length === users?.length;
  const isAllGroupSelected =
    Boolean(groups?.length) && selectedGroups?.length === groups?.length;
  const theme = useTheme();
  const useGreenStyleForGroups = hideCommunitySelector;
  const location = useLocation();
  const isEventPost = location.pathname.includes("/events");

  const groupHeaderClasses = clsx(
    "flex cursor-pointer justify-between items-center rounded-lg p-3",
    useGreenStyleForGroups ||
      (singleComGroupChoice === "community" && communities?.length === 1)
      ? classes.colorGreen
      : "bg-gray-100",
  );
  const useGreenStyleForConnections =
    singleComGroupChoice === "community" && groups?.length === 1;

  useEffect(() => {
    const checkGroupVisibility = async () => {
      try {
        let gid = null;
        if (groupId) gid = groupId;
        else if (selectedGroups && selectedGroups.length === 1)
          gid = selectedGroups[0];
        if (!gid) {
          setGroupIsPrivate(false);
          return;
        }
        const res = await axios.get(`${GET_GROUP}/${gid}/1`);
        const grp = res.data.group || res.data;
        const isPrivate = grp && grp.visibility === "Private";
        setGroupIsPrivate(Boolean(isPrivate));
        if (isPrivate) dispatch(clearConnections());
        // If the group is public and belongs to a community, and no
        // community selection is present, fetch community-wide connections
        // so the Invite Connections list includes all community members.
        if (!isPrivate && grp && grp.community) {
          try {
            const communityId = grp.community.id || grp.community;
            // Only fetch community connections when there is no explicit
            // selectedPostCommunities — otherwise the normal flow will
            // populate users from selected communities.
            if (
              !selectedPostCommunities ||
              selectedPostCommunities.length === 0
            ) {
              const conRes = await axios.get("/community-joined-connections", {
                params: { communityIds: [communityId] },
              });
              if (conRes && conRes.data) {
                setUsers(conRes.data);
                // Auto-select connections in create mode if appropriate
                const connectionsAlreadyInitialized = Boolean(
                  localStorage.getItem("post_connections_initialized"),
                );
                if (
                  !isEditMode &&
                  !connectionsAlreadyInitialized &&
                  conRes.data &&
                  conRes.data.length &&
                  (!connections || connections.length === 0)
                ) {
                  dispatch(addAllToConnections(conRes.data.map((u) => u.id)));
                }
              }
            }
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        setGroupIsPrivate(false);
      }
    };
    checkGroupVisibility();
  }, [groupId, selectedGroups]);

  useEffect(() => {
    if (userRecord?.id) {
      setLoading(true);
      axios
        .get(GET_COMMUNITIES)
        .then((response) => {
          let unbannedCommunities = response.data.filter((com) => {
            return (
              !userRecord.communitiesBanned.some((c) => c.id === com.id) &&
              com.disabledAt === 0
            );
          });
          if (!hideCommunitySelector && unbannedCommunities.length === 1) {
            setSingleComGroupChoice("community");
            dispatch(addToPostCommunities(unbannedCommunities[0].id));
            // ✅ only clear groups in create mode
            if (!isGroupOnlyPost && !isEditMode) dispatch(clearGroups());
          }
          setCommunities(unbannedCommunities);
          setLoading(false);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [userRecord]);

  useEffect(() => {
    if (!singleCommunityDataFetched) {
      if (!firstRender.current) {
        const hasPreselectedData =
          selectedGroups?.length > 0 || connections?.length > 0;
        // ✅ never clear in edit mode — parent already restored selections
        if (!isEditMode) {
          if (!isGroupOnlyPost && !hasPreselectedData) dispatch(clearGroups());
          if (!hasPreselectedData) dispatch(clearConnections());
        }
      }

      if (selectedPostCommunities?.length) {
        setCommunitySelectionLoading(true);
        Promise.all([
          axios.get("/community-joined-groups", {
            params: { communityIds: selectedPostCommunities },
          }),
          axios.get("/community-joined-connections", {
            params: { communityIds: selectedPostCommunities },
          }),
        ])

          .then((responses) => {
            const tempGroups = responses[0].data;
            const tempUsers = responses[1].data;

            let filteredGroups = tempGroups || [];

            // ✅ Apply filter ONLY for event posts — show groups where user is admin or moderator
            if (isEventPost && userRecord?.id) {
              const uid = String(userRecord.id);
              filteredGroups = (tempGroups || []).filter((g) => {
                // admin may be a populated object or a raw ID
                const adminId = g?.admin;
                const isAdmin = String(adminId) === uid;

                // moderators is a collection — each entry may be object or raw ID
                const isModerator = g?.moderators?.some(
                  (m) => m?.id === userRecord?.id,
                );
                return isAdmin || isModerator;
              });
            }

            // Use the filteredGroups for rendering and subsequent logic so that
            // event posts only show groups where the user is admin/moderator.
            setGroups(filteredGroups);
            setUsers(tempUsers);

            // ✅ In edit mode, re-apply original group selections after the
            // available groups list is built so checkboxes render as checked
            if (isEditMode && originalGroupIds.length > 0) {
              const validIds = (filteredGroups || [])
                .map((g) => String(g.id))
                .filter((id) => originalGroupIds.includes(id));
              if (validIds.length > 0) {
                dispatch(addAllToGroups(validIds));
              }
            }

            // Auto-select all connections ONLY in create mode
            const connectionsAlreadyInitialized = Boolean(
              localStorage.getItem("post_connections_initialized"),
            );
            if (
              !isEditMode &&
              !connectionsAlreadyInitialized &&
              tempUsers &&
              tempUsers.length &&
              (!connections || connections.length === 0)
            ) {
              dispatch(addAllToConnections(tempUsers.map((u) => u.id)));
            }

            if (
              singleComGroupChoice === "community" &&
              filteredGroups?.length === 1
            ) {
              // ✅ only auto-select single group in create mode
              if (!isEditMode) dispatch(addToGroups(filteredGroups[0].id));
              setSingleCommunityDataFetched(true);
            } else if (singleComGroupChoice === "community") {
              setSingleCommunityDataFetched(true);
            }
          })
          .catch((error) => console.log(error))
          .finally(() => setCommunitySelectionLoading(false));
      } else {
        setGroups([]);
        setUsers([]);
      }
    }
  }, [selectedPostCommunities]);

  const selectAllConnections = (e) => {
    e.stopPropagation();
    setShowConnections(true);
    if (connections.length !== users.length) {
      dispatch(addAllToConnections(users.map((user) => user.id)));
    } else {
      dispatch(clearConnections([]));
    }
  };

  const selectAllGroups = (e, groupId) => {
    e.stopPropagation();
    setShowGroups(true);
    if (isEventPost) {
      if (selectedGroups.length !== groups.length) {
        dispatch(addAllToGroups(groups.map((group) => group.id)));
      } else {
        dispatch(clearGroups([]));
      }
    } else {
      if (selectedGroups.includes(groupId)) {
        dispatch(clearGroups([]));
      } else {
        dispatch(clearGroups([]));
        dispatch(addToGroups(groupId));
      }
    }
  };

  const handleSingleComGroupChoice = (choice) => {
    setSingleComGroupChoice(choice);
    if (choice === "community" && communities.length === 1) {
      dispatch(addToPostCommunities(communities[0].id));
      dispatch(clearGroups());
    } else if (choice === "group") {
      if (selectedGroups?.[0] == groups?.[0]?.id) dispatch(clearGroups());
      else if (groups?.length === 1) dispatch(addToGroups(groups[0].id));
    }
  };

  useEffect(() => {
    if (firstRender.current) firstRender.current = false;
  }, []);

  const nextDisabled =
    !Boolean(selectedPostCommunities?.length) &&
    !Boolean(selectedGroups?.length) &&
    !Boolean(connections?.length);
  const isCommunitySelected = Boolean(selectedPostCommunities?.length);
  if (loading) return <Loader />;

  return (
    <div>
      {!hideCommunitySelector && !isGroupOnlyPost && !loading && communities.length > 1 && (
        <div className="my-5">
          <div
            onClick={() => setShowCommunities(!showCommunities)}
            className={clsx(
              classes.colorGreen,
              "flex cursor-pointer justify-between items-center rounded-lg p-3",
            )}
          >
            {showCommunities ? (
              <ArrowForwardIcon fontSize="small" className="rotate-90 mr-3" />
            ) : (
              <ArrowForwardIcon fontSize="small" className="mr-3" />
            )}
            <Typography className="font-semibold">
              Select Community (Required)
            </Typography>
            <div className="ml-auto"></div>
          </div>
          {showCommunities && (
            <div className="mt-2 mx-5">
              <SelectPostCom communities={communities} />
            </div>
          )}
        </div>
      )}

      {isCommunitySelected &&
        (Boolean(groups?.length) || Boolean(users?.length)) && (
          <div className="border border-gray-200 rounded-lg mx-5 p-3 mt-5">
            {Boolean(groups?.length) &&
              !(
                singleComGroupChoice === "community" && groups.length === 1
              ) && (
                <div className="mb-4">
                  <div
                    onClick={() => setShowGroups(!showGroups)}
                    className={groupHeaderClasses}
                  >
                    {showGroups ? (
                      <ArrowForwardIcon
                        fontSize="small"
                        className={clsx(
                          "rotate-90 mr-3",
                          useGreenStyleForGroups,
                        )}
                      />
                    ) : (
                      <ArrowForwardIcon
                        fontSize="small"
                        className={clsx(
                          "mr-3",
                          useGreenStyleForGroups ? "text-green-700" : "",
                        )}
                      />
                    )}
                    <Typography
                      className={clsx(classes.textGrey, "font-semibold")}
                    >
                      {isEventPost
                        ? "Invite Groups (Optional)"
                        : "Select Groups (Optional)"}
                    </Typography>
                    <div className="ml-auto"></div>
                    {isEventPost && (
                      <Typography
                        className={clsx("font-semibold cursor-pointer", "mr-3")}
                        color={theme.palette.primary.main}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectAllGroups(e);
                        }}
                      >
                        {isAllGroupSelected ? "Clear" : "Select All"}
                      </Typography>
                    )}
                  </div>
                  {showGroups && (
                    <SelectGroup groups={groups} singleSelect={!isEventPost} />
                  )}
                </div>
              )}

            {Boolean(users?.length > 0) && !groupIsPrivate && (
              <div className="mt-4">
                <div
                  onClick={() => setShowConnections(!showConnections)}
                  className={clsx(
                    "flex cursor-pointer items-center rounded-lg p-3",
                    useGreenStyleForConnections
                      ? classes.colorGreen
                      : "bg-gray-100",
                  )}
                >
                  {showConnections ? (
                    <ArrowForwardIcon
                      fontSize="small"
                      className="rotate-90 mr-3"
                    />
                  ) : (
                    <ArrowForwardIcon fontSize="small" className="mr-3" />
                  )}
                  <Typography
                    className={clsx(classes.textGrey, "font-semibold")}
                  >
                    {isEventPost
                      ? "Invite Connections (Optional)"
                      : "Select Connections (Optional)"}
                  </Typography>
                  <div className="ml-auto"></div>
                  <Typography
                    className={clsx("font-semibold cursor-pointer", "mr-3")}
                    color={theme.palette.primary.main}
                    onClick={(e) => {
                      e.stopPropagation();
                      selectAllConnections(e, isAllSelected);
                    }}
                  >
                    {isAllSelected ? "Clear" : "Select All"}
                  </Typography>
                </div>
                {showConnections && <SelectConn users={users} />}
              </div>
            )}
          </div>
        )}
    </div>
  );
}

export default AddComCon;
